/**
 * The six onboarding steps and whether each is done, read from the tenant
 * itself rather than stored: a stored checklist can say "done" for a step
 * that was later undone. The builder is pure; the hook gathers its reads.
 *
 * Came out of the retired onboarding page unchanged in its rules; the home
 * draws the result as a card until every step is done (Feature 70, D10).
 */
import { listAgents } from '@optum/api-client/endpoints/agents';
import { listOrgSecrets } from '@optum/api-client/endpoints/organization-secrets';
import { getAdminOrganization } from '@optum/api-client/endpoints/organizations';
import { listTasks } from '@optum/api-client/endpoints/tasks';
import { useQuery } from '@tanstack/react-query';

import { qk } from '../../lib/query-keys.js';

export type StepState = 'done' | 'partial' | 'todo';

export interface StepRow {
  label: string;
  hint: string;
  state: StepState;
  stateLabel: string;
  icon: string;
}

export interface OnboardingStep {
  n: number;
  title: string;
  description: string;
  done: boolean;
  cta: string;
  to: string;
  rows: StepRow[];
}

export interface OnboardingInput {
  orgId: string;
  active: boolean;
  secrets: { field_key: string; label: string; is_set: boolean }[];
  agentCount: number;
  agentNames: string[];
  tasks: { status: string; source: string }[];
}

export const ONBOARDING_TOTAL = 5;

export function buildOnboardingSteps(input: OnboardingInput): OnboardingStep[] {
  const base = `/orgs/${input.orgId}`;
  const setSecrets = input.secrets.filter((s) => s.is_set);
  const succeeded = input.tasks.some((t) => t.status === 'completed');

  return [
    {
      n: 1,
      title: 'Approve',
      description:
        'The registration is approved and the organization is active. Everything below is scoped to it.',
      done: input.active,
      cta: 'See the status',
      to: `${base}/overview`,
      rows: [
        {
          label: 'Organization activated',
          hint: input.active ? 'status is active' : 'not active yet',
          state: input.active ? 'done' : 'todo',
          stateLabel: input.active ? 'active' : 'pending',
          icon: 'apartment',
        },
      ],
    },
    {
      n: 2,
      title: 'Secrets',
      description:
        'Every capability resolves credentials from the secret store. A missing value fails at submission, not at configuration — which is why this comes before anything that runs.',
      // Inheriting the platform value is a legitimate answer, so the step is
      // done when the catalogue is empty or anything has been decided for it.
      done: input.secrets.length === 0 || setSecrets.length > 0,
      cta: 'Set secrets',
      to: `${base}/secrets`,
      rows: input.secrets.slice(0, 5).map((s) => ({
        label: s.field_key,
        hint: s.is_set ? 'set for this tenant' : 'inherits the platform value',
        state: s.is_set ? ('done' as const) : ('partial' as const),
        stateLabel: s.is_set ? 'set' : 'inherited',
        icon: 'key',
      })),
    },
    {
      n: 3,
      title: 'Capabilities',
      description:
        'Override the platform defaults where this tenant needs different values. Inheriting everything is a valid answer; the Resolved tab shows what a task would actually run with.',
      done: true,
      cta: 'Open configuration',
      to: `${base}/config`,
      rows: [
        {
          label: 'Capability configuration',
          hint: 'inherited unless overridden — nothing is required here',
          state: 'done',
          stateLabel: 'optional',
          icon: 'settings',
        },
      ],
    },
    {
      n: 4,
      title: 'First agent',
      description:
        'An agent is the actor that runs tasks. It owns the capability allow-list, so nothing can execute for this tenant until one exists.',
      done: input.agentCount > 0,
      cta: input.agentCount > 0 ? 'Open agents' : 'Create an agent',
      to: `${base}/agents`,
      rows:
        input.agentCount > 0
          ? input.agentNames.slice(0, 4).map((name) => ({
              label: name,
              hint: 'agent exists',
              state: 'done' as const,
              stateLabel: 'ready',
              icon: 'smart_toy',
            }))
          : [
              {
                label: 'No agent yet',
                hint: 'tasks cannot run for this tenant without one',
                state: 'todo',
                stateLabel: 'required',
                icon: 'smart_toy',
              },
            ],
    },
    {
      n: 5,
      title: 'Smoke test',
      description:
        'Submit one real task. This is the only step that proves secrets, configuration, agent and capability resolve together — every step above can look correct and still fail here.',
      done: succeeded,
      cta: 'Open the sandbox',
      to: '/sandbox',
      rows: [
        {
          label: 'A task has completed',
          hint: succeeded
            ? 'the tenant has produced a successful run'
            : input.tasks.length > 0
              ? `${input.tasks.length} task${input.tasks.length === 1 ? '' : 's'} submitted, none completed`
              : 'no task has run for this tenant',
          state: succeeded ? 'done' : input.tasks.length > 0 ? 'partial' : 'todo',
          stateLabel: succeeded ? 'passed' : input.tasks.length > 0 ? 'in progress' : 'waiting',
          icon: 'science',
        },
      ],
    },
  ];
}

export interface OnboardingProgress {
  steps: OnboardingStep[];
  done: number;
  total: number;
  /** True until every read has answered once; the card waits for it. */
  loading: boolean;
}

/** The reads behind the checklist, shared with the page through the query cache. */
export function useOnboardingSteps(orgId: string): OnboardingProgress {
  const org = useQuery({ queryKey: qk.org(orgId), queryFn: () => getAdminOrganization(orgId) });
  const secrets = useQuery({ queryKey: qk.orgSecrets(orgId), queryFn: () => listOrgSecrets(orgId) });
  const agents = useQuery({
    queryKey: qk.agents(orgId),
    queryFn: () => listAgents({ organization_id: orgId }),
  });
  const tasks = useQuery({
    queryKey: qk.tasks({ organization_id: orgId }),
    queryFn: () => listTasks({ organization_id: orgId, limit: 50 }),
  });
  const steps = buildOnboardingSteps({
    orgId,
    active: org.data?.status === 'active',
    secrets: secrets.data ?? [],
    agentCount: agents.data?.length ?? 0,
    agentNames: (agents.data ?? []).map((a) => a.name),
    tasks: tasks.data ?? [],
  });
  return {
    steps,
    done: steps.filter((s) => s.done).length,
    total: ONBOARDING_TOTAL,
    loading: [org, secrets, agents, tasks].some((q) => q.isPending),
  };
}
